import { DeferredPromise, BinaryMessage, ErrorUtils } from '../common/index.js';
export const DXTPConnectionState = {
    Pending: 'Pending',
    Connected: 'Connected',
    Disconnected: 'Disconnected'
};
const RESPONSE_TYPE = '__DXTP_RESPONSE';
function binaryReplacer(key, value) {
    // Node.js Buffer detection
    const isNodeBuffer = typeof Buffer !== "undefined" && typeof Buffer.isBuffer === "function" && Buffer.isBuffer(value);
    // Browser ArrayBuffer or Uint8Array detection
    const isUint8Array = value instanceof Uint8Array;
    // Serialized Buffer detection (e.g., { type: 'Buffer', data: [...] })
    const isSerializedBuffer = value &&
        typeof value === "object" &&
        value.type === "Buffer" &&
        Array.isArray(value.data);
    if (isNodeBuffer || isUint8Array) {
        return `<binary ${value.byteLength} bytes>`;
    }
    if (isSerializedBuffer) {
        return `<binary ${value.data.length} bytes>`;
    }
    return value;
}
export class DXTPConnection {
    constructor(options) {
        this.socket = null;
        this.pendingSocketMessages = [];
        this.outgoingRequestPromises = new Map();
        this.outgoingNextRequestId = 1;
        this.state = DXTPConnectionState.Disconnected;
        this.incomingRequestHandler = null;
        this.socketMessageHandler = this.onSocketMessage.bind(this);
        this.socketErrorHandler = this.onSocketError.bind(this);
        this.options = options;
        if (options?.initialState !== undefined) {
            this.state = options.initialState;
        }
    }
    setState(state) {
        if (state === this.state) {
            return;
        }
        console.log(`DXTP connection state [${state}]: [${this.socket?.getAddress()}]`);
        this.state = state;
        if (this.state == DXTPConnectionState.Connected) {
            const tmpMessages = this.pendingSocketMessages;
            this.pendingSocketMessages = [];
            for (const item of tmpMessages) {
                try {
                    this.sendSocketMessage(item.message, item.isBinary);
                }
                catch (error) {
                    console.error(`Failed to flush pending request`);
                }
            }
        }
        else if (this.state == DXTPConnectionState.Disconnected) {
            this.pendingSocketMessages = [];
            if (!this.options?.silentDisconnect) {
                this.outgoingRequestPromises.forEach(value => value.reject('Connection disconnected'));
            }
            this.outgoingRequestPromises.clear();
        }
    }
    getState() {
        return this.state;
    }
    setIncomingRequestHandler(requestHandler) {
        this.incomingRequestHandler = requestHandler;
    }
    setSocket(socket) {
        if (this.socket) {
            this.socket.reset();
        }
        this.socket = socket;
        if (this.socket) {
            this.socket.setMessageHandler(this.socketMessageHandler);
            this.socket.setErrorHandler(this.socketErrorHandler);
        }
    }
    getSocket() {
        return this.socket;
    }
    sendRequest(header, body) {
        if (this.state === DXTPConnectionState.Disconnected) {
            throw new Error('sendRequest: connection disconnected');
        }
        if (!header || !header.type) {
            throw new Error(`type is missing in request header [${JSON.stringify(header)}]`);
        }
        const requestId = this.outgoingNextRequestId++;
        const requestMsg = {
            header: { ...header, ...{ id: requestId, type: header.type } },
            body: body
        };
        this.sendSocketMessage(requestMsg, true);
        if (header.no_reply) {
            return Promise.resolve(null);
        }
        const promise = new DeferredPromise();
        this.outgoingRequestPromises.set(requestId, promise);
        return promise.promise();
    }
    handleIncomingResponse(response) {
        const requestPromise = this.outgoingRequestPromises.get(response.header.request_id);
        if (!requestPromise) {
            console.error(`Unknown request_id [${response.header.request_id}] in response`);
            return;
        }
        if (response.body.error) {
            requestPromise.reject(response.body.error);
        }
        else {
            requestPromise.resolve(response.body.data);
        }
        this.outgoingRequestPromises.delete(response.header.request_id);
    }
    async handleIncomingRequest(request) {
        let response = null;
        let error = null;
        try {
            if (this.incomingRequestHandler) {
                response = await this.incomingRequestHandler(request);
            }
            else {
                throw new Error(`Incoming request handler is not set`);
            }
        }
        catch (err) {
            error = ErrorUtils.errorToString(err);
        }
        if (request.header.no_reply) {
            if (error) {
                console.error(`Error response ignored. Error: ${error}`);
            }
            return null;
        }
        const respMsg = {
            header: {
                type: RESPONSE_TYPE,
                request_id: request.header.id
            },
            body: {}
        };
        if (error) {
            console.error(error);
            respMsg.body.error = error;
        }
        if (response) {
            respMsg.body.data = response;
        }
        return respMsg;
    }
    async sendSocketMessage(message, isBinary) {
        //console.log(`---------- DXTP send message [${JSON.stringify(message, binaryReplacer)}]`);
        if (this.state === DXTPConnectionState.Pending) {
            //console.log(`---------- DXTP send message - caching [${JSON.stringify(message.header.type)}]`);
            this.pendingSocketMessages.push({ message, isBinary });
            return;
        }
        let messageBin = null;
        if (isBinary) {
            messageBin = BinaryMessage.encode(message);
        }
        else {
            messageBin = JSON.stringify(message);
        }
        if (!messageBin) {
            console.error(`Failed to encode message [${JSON.stringify(message, binaryReplacer)}]`);
            return;
        }
        try {
            this.socket?.send(messageBin);
        }
        catch (error) {
            console.error(`Failed to send message. Error: ${error.toString()}`);
        }
    }
    async onSocketMessage(socketMessage) {
        const isBinary = (socketMessage instanceof Uint8Array);
        let message = null;
        try {
            if (isBinary) {
                message = BinaryMessage.decode(socketMessage);
            }
            else {
                message = JSON.parse(socketMessage);
            }
        }
        catch (error) {
            console.error(`DXTP parse request error: ${error}`);
            return;
        }
        if (!message) {
            console.error("Received bad request. Skipped.");
            return;
        }
        //console.log(`---------- DXTP received message [${JSON.stringify(message, binaryReplacer)}]`);
        if (message.header.type === RESPONSE_TYPE) {
            this.handleIncomingResponse(message);
        }
        else {
            const respMsg = await this.handleIncomingRequest(message);
            if (respMsg) {
                this.sendSocketMessage(respMsg, isBinary);
            }
        }
    }
    onSocketError(error) {
        console.error(`DXTP socket error [${this.socket?.getAddress()}]: ${error}`);
        if (this.socket) {
            this.socket.close();
        }
    }
}
//# sourceMappingURL=DXTPConnection.js.map